import React, { useState, useRef, useEffect } from 'react';
import jsQR from 'jsqr';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const QRPackageScanner = ({ onScan, scannedData }) => {
  const [scanning, setScanning] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [error, setError] = useState(null);
  const [lastScan, setLastScan] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    return () => {
      stopScanning();
    };
  }, []);

  useEffect(() => {
    if (scannedData) {
      setLastScan({
        data: scannedData,
        timestamp: new Date()
      });
    }
  }, [scannedData]);

  const scanFrame = () => {
    if (!scanning || !videoRef?.current || !canvasRef?.current) return;

    const video = videoRef?.current;
    const canvas = canvasRef?.current;
    const context = canvas?.getContext('2d');

    if (video?.readyState === video?.HAVE_ENOUGH_DATA) {
      canvas.height = video?.videoHeight;
      canvas.width = video?.videoWidth;
      context?.drawImage(video, 0, 0, canvas?.width, canvas?.height);
      
      const imageData = context?.getImageData(0, 0, canvas?.width, canvas?.height);
      const code = jsQR(imageData?.data, imageData?.width, imageData?.height);

      if (code) {
        handleScanSuccess(code?.data);
        return;
      }
    }

    requestAnimationFrame(scanFrame);
  };

  const handleScanSuccess = (data) => {
    setLastScan({
      data,
      timestamp: new Date()
    });
    stopScanning();
    onScan(data);
  };

  const startScanning = async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices?.getUserMedia({
        video: { facingMode: 'environment' }
      });
      
      streamRef.current = stream;
      if (videoRef?.current) {
        videoRef.current.srcObject = stream;
        videoRef?.current?.play();
        setScanning(true);
        requestAnimationFrame(scanFrame);
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Impossible d\'accéder à la caméra. Veuillez utiliser la saisie manuelle.');
    }
  };

  const stopScanning = () => {
    if (streamRef?.current) {
      streamRef?.current?.getTracks()?.forEach(track => track?.stop());
      streamRef.current = null;
    }
    setScanning(false);
  };

  const handleManualSubmit = (e) => {
    e?.preventDefault();
    if (manualInput?.trim()) {
      handleScanSuccess(manualInput);
      setManualInput('');
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Camera Scanner */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Scanner le QR Code du colis</h3>
        
        <div className="relative bg-gray-900 rounded-lg overflow-hidden mb-4" style={{ aspectRatio: '4/3' }}>
          {scanning ? (
            <>
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                playsInline
              />
              <canvas ref={canvasRef} className="hidden" />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-64 h-64 border-4 border-primary rounded-lg animate-pulse"></div>
              </div>
              <div className="absolute bottom-4 left-0 right-0 text-center">
                <p className="text-white text-sm bg-black/50 inline-block px-4 py-2 rounded-full">
                  Positionnez le QR code dans le cadre
                </p>
              </div>
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-center">
                <Icon name="Camera" size={64} className="mx-auto text-gray-600 mb-4" />
                <p className="text-white text-sm">Caméra inactive</p>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="bg-error/10 border border-error/30 rounded-lg p-3 mb-4 text-sm text-error">
            {error}
          </div>
        )}

        <div className="flex gap-2">
          {!scanning ? (
            <Button
              variant="primary"
              iconName="Camera"
              onClick={startScanning}
              className="flex-1"
            >
              Démarrer le scan
            </Button>
          ) : (
            <Button
              variant="outline"
              iconName="X"
              onClick={stopScanning}
              className="flex-1"
            >
              Arrêter le scan
            </Button>
          )}
        </div>
      </div>
      {/* Manual Input & Instructions */}
      <div className="space-y-6">
        {/* Manual Input */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Saisie manuelle</h3>
          <form onSubmit={handleManualSubmit} className="space-y-4">
            <Input
              type="text"
              placeholder="DELIVERY-XXXX-TIMESTAMP"
              value={manualInput}
              onChange={(e) => setManualInput(e?.target?.value)}
            />
            <Button
              type="submit"
              variant="primary"
              iconName="Check"
              disabled={!manualInput?.trim()}
              className="w-full"
            >
              Valider le code
            </Button>
          </form>
        </div>

        {/* Last Scan */}
        {lastScan && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Dernier scan</h3>
            <div className="bg-success/10 border border-success/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="CheckCircle" size={20} className="text-success mt-0.5" />
                <div className="flex-1">
                  <div className="font-mono text-sm text-gray-900 mb-1 break-all">{lastScan?.data}</div>
                  <div className="text-xs text-gray-600">
                    {lastScan?.timestamp?.toLocaleString('fr-FR')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Instructions</h3>
          <ul className="text-sm text-gray-600 space-y-2">
            <li className="flex items-start gap-2">
              <Icon name="CheckCircle" size={16} className="text-primary mt-0.5" />
              <span>Scannez le QR code sur votre colis ou étiquette de livraison</span>
            </li>
            <li className="flex items-start gap-2">
              <Icon name="CheckCircle" size={16} className="text-primary mt-0.5" />
              <span>Le scan se fait automatiquement dès détection</span>
            </li>
            <li className="flex items-start gap-2">
              <Icon name="CheckCircle" size={16} className="text-primary mt-0.5" />
              <span>Utilisez la saisie manuelle si le scan ne fonctionne pas</span>
            </li>
            <li className="flex items-start gap-2">
              <Icon name="CheckCircle" size={16} className="text-primary mt-0.5" />
              <span>Vous verrez instantanément le statut de livraison</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default QRPackageScanner;